import type { ColumnTable } from 'arquero';

import { inferColumnSchemas } from '#core/lib/transforms/column-schema.ts';

import { datasetToTable } from './dataset-to-table.ts';
import { addNestedColumns } from './nested-paths.ts';
import { createDataset } from '#plugin-chat/lib/data-pipeline/dataset.ts';
import type { ColumnSchema, Dataset, DatasetEnvelope } from '#plugin-chat/lib/data-pipeline/types.ts';
import { ColumnNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';

/** Spec for joining two datasets. */
export interface JoinSpec {
  on: { left: string; right: string };
  how: 'left' | 'inner' | 'right' | 'full';
}

/** Suffix appended to right-table columns that collide with left-table columns. */
const CONFLICT_SUFFIX = '_right';

/**
 * Prefix for synthetic columns used when a join key is a dot-notation path
 * (e.g. `well.externalId`). Arquero's `.join()` requires flat column names,
 * so we materialise the nested value into a hidden column, join on it, and
 * strip it from the output — the caller only ever sees their original
 * columns plus whatever conflict-renamed right-side columns emerge.
 *
 * Exported so tests can assert on the prefix rather than hard-coding each
 * synthetic column name — SSOT for the "synthetic column" contract.
 */
export const SYNTHETIC_JOIN_KEY_PREFIX = '__nyne_join_';
const SYNTHETIC_LEFT_KEY = `${SYNTHETIC_JOIN_KEY_PREFIX}left_key`;
const SYNTHETIC_RIGHT_KEY = `${SYNTHETIC_JOIN_KEY_PREFIX}right_key`;
export interface JoinResult extends DatasetEnvelope {
  success: true;
}

/**
 * Result of resolving a join key against a dataset's column schema.
 *
 * Keys can be either flat column names (`dept`) or dot-notation paths
 * into nested object columns (`well.externalId`). Nested paths require
 * synthesising a flat column on the Arquero table because `.join()` only
 * operates on column names, not arbitrary expressions.
 */
type ResolvedJoinKey =
  | { ok: true; flatName: string; needsSynthesis: false }
  | { ok: true; flatName: string; needsSynthesis: true; path: string }
  | { ok: false };

/**
 * Resolve a join key against a dataset's column schema.
 *
 * Mirrors the flat-first semantics of `getNestedValue` / `applySelect`:
 * a literal column-name match wins (handles columns whose names contain
 * dots), otherwise the first dot-separated segment must exist as a
 * top-level column so the nested walk has somewhere to start.
 */
function resolveJoinKey(dataset: Dataset, path: string, side: 'left' | 'right'): ResolvedJoinKey {
  const colNames = new Set(dataset.columns.map((c) => c.name));

  if (colNames.has(path)) {
    return { ok: true, flatName: path, needsSynthesis: false };
  }

  const dotIndex = path.indexOf('.');
  if (dotIndex === -1) {
    return { ok: false };
  }

  const parent = path.substring(0, dotIndex);
  if (!colNames.has(parent)) {
    return { ok: false };
  }

  const flatName = side === 'left' ? SYNTHETIC_LEFT_KEY : SYNTHETIC_RIGHT_KEY;
  return { ok: true, flatName, needsSynthesis: true, path };
}

/**
 * Materialise a flat synthetic key column on an Arquero table from a
 * dot-notation path. Thin wrapper around {@link addNestedColumns} — the
 * canonical primitive lives in `nested-paths.ts` so this site and the
 * executor's pre-materialisation pass share one round-trip implementation.
 */
function addSyntheticKey(table: ColumnTable, key: string, path: string): ColumnTable {
  return addNestedColumns(table, [{ key, path }]);
}
/**
 * Join two datasets using Arquero, producing a single combined dataset.
 *
 * The left dataset is the primary (source) table; columns from the right
 * dataset are appended. When non-key columns share a name, the right
 * column is suffixed with {@link CONFLICT_SUFFIX}.
 *
 * Join keys accept either flat column names or dot-notation paths into
 * nested object columns (e.g. `well.externalId`), matching the semantics
 * of filter and select operators. Nested paths are materialised into a
 * hidden flat column for the duration of the join and stripped from the
 * final output.
 *
 * Column schemas are merged: left columns are preserved as-is, right
 * columns (excluding a flat join key on the right side) are appended —
 * with conflict-renamed names when necessary. Columns that don't appear
 * in either input schema (e.g. coalesced key columns in full joins) are
 * inferred from the output.
 *
 * Throws {@link ColumnNotFoundError} when a join key resolves to neither
 * a flat column nor a dot-notation path on its respective dataset.
 */
export function joinDatasets(left: Dataset, right: Dataset, spec: JoinSpec): JoinResult {
  const { on, how } = spec;

  const leftColNames = left.columns.map((c) => c.name);
  const rightColNames = right.columns.map((c) => c.name);
  const leftColNameSet = new Set(leftColNames);

  const leftResolved = resolveJoinKey(left, on.left, 'left');
  if (!leftResolved.ok) {
    throw new ColumnNotFoundError('join (left side)', [on.left], leftColNames);
  }

  const rightResolved = resolveJoinKey(right, on.right, 'right');
  if (!rightResolved.ok) {
    throw new ColumnNotFoundError('join (right side)', [on.right], rightColNames);
  }

  // Build rename map for conflicting right-table columns. When the right key
  // is a flat column, Arquero keeps the left's copy and drops the right's —
  // so we exclude it from the rename map. When the right key is a nested
  // path, the right's top-level columns (e.g. `well`) remain in the output
  // and still need conflict-renaming if they collide with left-side names.
  const rightRenameMap: Record<string, string> = Object.fromEntries(
    rightColNames
      .filter((col) => rightResolved.needsSynthesis || col !== on.right)
      .filter((col) => leftColNameSet.has(col))
      .map((col) => [col, `${col}${CONFLICT_SUFFIX}`]),
  );

  let tableL = datasetToTable(left);
  let tableR = datasetToTable(right);

  if (leftResolved.needsSynthesis) {
    tableL = addSyntheticKey(tableL, SYNTHETIC_LEFT_KEY, leftResolved.path);
  }
  if (rightResolved.needsSynthesis) {
    tableR = addSyntheticKey(tableR, SYNTHETIC_RIGHT_KEY, rightResolved.path);
  }

  // Rename conflicting right columns before joining so Arquero doesn't
  // auto-suffix them with _1/_2 in an unpredictable way.
  const renamedR = Object.keys(rightRenameMap).length > 0 ? tableR.rename(rightRenameMap) : tableR;

  const joinKeys = { left: leftResolved.flatName, right: rightResolved.flatName };
  const joined = executeJoin(tableL, renamedR, joinKeys, how);

  // Strip synthetic key columns from the output rows. Arquero auto-drops
  // the right-side join key for inner/left/right joins, but a full join
  // retains it as a coalesced column — so handle both sides defensively.
  const rawRows = joined.objects() as Record<string, unknown>[];
  const rows =
    leftResolved.needsSynthesis || rightResolved.needsSynthesis
      ? rawRows.map((row) => {
          const clean: Record<string, unknown> = { ...row };
          if (leftResolved.needsSynthesis) {
            delete clean[SYNTHETIC_LEFT_KEY];
          }
          if (rightResolved.needsSynthesis) {
            delete clean[SYNTHETIC_RIGHT_KEY];
          }
          return clean;
        })
      : rawRows;

  const columns = mergeColumnSchemas(left.columns, right.columns, on, rightRenameMap, rows);

  return {
    success: true,
    dataset: createDataset({
      rows,
      columns,
      meta: { joinStats: { leftRowCount: left.rows.length, rightRowCount: right.rows.length } },
    }),
  };
}

/** Dispatch to the appropriate Arquero join method. */
function executeJoin(tableL: ColumnTable, tableR: ColumnTable, on: JoinSpec['on'], how: JoinSpec['how']): ColumnTable {
  const keys: [string, string] = [on.left, on.right];

  switch (how) {
    case 'inner':
      return tableL.join(tableR, keys);
    case 'left':
      return tableL.join_left(tableR, keys);
    case 'right':
      return tableL.join_right(tableR, keys);
    case 'full':
      return tableL.join_full(tableR, keys);
  }
}

/**
 * Merge column schemas from left and right datasets.
 *
 * - Left columns are preserved in order.
 * - Right columns (excluding the join key) are appended, using the renamed
 *   name when a conflict exists.
 * - Columns present in the final rows but not in either input schema
 *   (e.g. coalesced key from full joins) are inferred from row data.
 */
function mergeColumnSchemas(
  leftColumns: readonly ColumnSchema[],
  rightColumns: readonly ColumnSchema[],
  on: JoinSpec['on'],
  rightRenameMap: Record<string, string>,
  finalRows: Record<string, unknown>[],
): ColumnSchema[] {
  const merged: ColumnSchema[] = [...leftColumns];
  const mergedNames = new Set(merged.map((c) => c.name));

  for (const col of rightColumns) {
    if (col.name === on.right) {
      continue;
    }

    const finalName = rightRenameMap[col.name] ?? col.name;
    if (!mergedNames.has(finalName)) {
      merged.push({ ...col, name: finalName });
      mergedNames.add(finalName);
    }
  }

  // Safety net: infer any columns present in output rows but missing from
  // merged schemas (can happen with Arquero's key coalescing in full joins).
  if (finalRows.length > 0) {
    const inferred = inferColumnSchemas(finalRows);
    for (const col of inferred) {
      if (!mergedNames.has(col.name)) {
        merged.push(col);
        mergedNames.add(col.name);
      }
    }
  }

  return merged;
}
